<template>
  <div id="astra-chatbot">
    <button
      id="astra-widget-bubble"
      type="button"
      :style="bubbleStyle"
      @click="toggle"
    >
      <span v-if="isOpen" class="astra-bubble-icon">X</span>
      <span v-else class="astra-bubble-icon astra-bubble-svg">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 3C6.48 3 2 6.94 2 11.8c0 2.77 1.45 5.25 3.72 6.87L5 22l3.67-2.02c1.04.28 2.16.42 3.33.42 5.52 0 10-3.94 10-8.8S17.52 3 12 3zm-4 6h8a1 1 0 010 2H8a1 1 0 010-2zm0 4h5a1 1 0 010 2H8a1 1 0 010-2z"
          />
        </svg>
      </span>
    </button>

    <div
      id="astra-chat-window"
      :class="{ open: isOpen }"
      :style="windowBorderStyle"
    >
      <div class="astra-chat-header" :style="headerStyle">
        <div>
          <h3>Asistente LMSEDK</h3>
          <small>Asistente en linea</small>
        </div>
      </div>

      <div ref="messages" class="astra-messages">
        <div
          v-for="(message, index) in messages"
          :key="index"
          ref="messageItems"
          class="astra-message"
          :class="message.role"
          v-html="renderMessage(message)"
        />
        <div v-if="loading" class="astra-message ai">Escribiendo...</div>
      </div>

      <div class="astra-input-area">
        <input
          v-model.trim="draft"
          type="text"
          placeholder="Escribe un mensaje..."
          @keypress.enter="sendMessage"
        />
        <button
          type="button"
          class="astra-send-btn"
          :style="headerStyle"
          :disabled="loading || !draft"
          @click="sendMessage"
        >
          >
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AstraChatbot",
  props: {
    cia: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      draft: "",
      history: [],
      messages: [],
      config: {
        assistantName: "Astra LMS",
        primaryColor: "#1D3FA9",
        greetingMessage:
          "Hola, soy Astra. Puedo ayudarte con cursos, navegacion de la plataforma y dudas frecuentes del LMS.",
      },
    };
  },
  computed: {
    resolvedConfigId() {
      if (this.cia && this.cia.chatbot_config_id) {
        return this.cia.chatbot_config_id;
      }

      return "default";
    },
    headerStyle() {
      return {
        background: `linear-gradient(135deg, ${this.config.primaryColor}, ${this.shadeColor(
          this.config.primaryColor,
          -18
        )})`,
      };
    },
    bubbleStyle() {
      return {
        background: `linear-gradient(135deg, ${this.config.primaryColor}, ${this.shadeColor(
          this.config.primaryColor,
          -18
        )})`,
      };
    },
    windowBorderStyle() {
      return {
        borderColor: this.hexToRgba(this.config.primaryColor, 0.18),
      };
    },
  },
  watch: {
    resolvedConfigId: {
      immediate: true,
      handler() {
        this.fetchConfig();
      },
    },
    isOpen(open) {
      if (open) {
        this.scheduleScrollToBottom();
      }
    },
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      this.scheduleScrollToBottom();
    },
    fetchConfig() {
      this.$http
        .get(`astra/config/${this.resolvedConfigId}`)
        .then((response) => response.json())
        .then((config) => {
          this.config = Object.assign({}, this.config, config || {});
          this.messages = [{ role: "ai", text: this.config.greetingMessage }];
          this.history = [];
        })
        .catch(() => {
          this.messages = [{ role: "ai", text: this.config.greetingMessage }];
          this.history = [];
        });
    },
    sendMessage() {
      const text = this.draft;
      if (!text || this.loading) {
        return;
      }

      this.messages.push({ role: "user", text });
      this.history.push({ role: "user", content: text });
      this.draft = "";
      this.loading = true;
      this.scheduleScrollToBottom();

      this.$http
        .post("astra/chat", {
          message: text,
          configId: this.resolvedConfigId,
          history: this.history,
        })
        .then((response) => response.json())
        .then((data) => {
          const reply =
            data && data.response
              ? data.response
              : "No pude generar una respuesta en este momento.";

          this.messages.push({ role: "ai", text: reply });
          this.history.push({ role: "assistant", content: reply });
          this.scheduleScrollToLatestAiMessage();
        })
        .catch(() => {
          this.messages.push({
            role: "ai",
            text: "Lo siento, no puedo conectarme con el servicio de IA en este momento.",
          });
          this.scheduleScrollToLatestAiMessage();
        })
        .then(() => {
          this.loading = false;
        });
    },
    scheduleScrollToBottom() {
      this.$nextTick(() => {
        if (typeof window !== "undefined" && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              this.scrollToBottom();
            });
          });
          return;
        }

        setTimeout(() => {
          this.scrollToBottom();
        }, 0);
      });
    },
    scrollToBottom() {
      if (this.$refs.messages) {
        const container = this.$refs.messages;
        container.scrollTop = container.scrollHeight + 200;
      }
    },
    scheduleScrollToLatestAiMessage() {
      this.$nextTick(() => {
        if (typeof window !== "undefined" && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              this.scrollToLatestAiMessage();
            });
          });
          return;
        }

        setTimeout(() => {
          this.scrollToLatestAiMessage();
        }, 0);
      });
    },
    scrollToLatestAiMessage() {
      const container = this.$refs.messages;
      const items = this.$refs.messageItems;

      if (!container || !items || !items.length) {
        this.scrollToBottom();
        return;
      }

      for (let index = items.length - 1; index >= 0; index -= 1) {
        const element = items[index];
        const message = this.messages[index];

        if (element && message && message.role === "ai") {
          container.scrollTop = Math.max(element.offsetTop - 78, 0);
          return;
        }
      }

      this.scrollToBottom();
    },
    renderMessage(message) {
      const text = message && message.text ? String(message.text) : "";
      if (message && message.role === "ai") {
        return this.formatAssistantMessage(text);
      }

      return this.escapeHtml(text);
    },
    formatAssistantMessage(text) {
      return this.escapeHtml(text)
        .replace(
          /\*\*(.+?)\*\*/g,
          '<strong style="font-weight: 700 !important;">$1</strong>'
        )
        .replace(/\n/g, "<br>");
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    shadeColor(color, percent) {
      const normalized = (color || "").replace("#", "");
      if (normalized.length !== 6) {
        return "#16348c";
      }

      const num = parseInt(normalized, 16);
      const amt = Math.round(2.55 * percent);
      const r = Math.min(255, Math.max(0, (num >> 16) + amt));
      const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
      const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));

      return `#${(
        0x1000000 +
        r * 0x10000 +
        g * 0x100 +
        b
      )
        .toString(16)
        .slice(1)}`;
    },
    hexToRgba(color, alpha) {
      const normalized = (color || "").replace("#", "");
      if (normalized.length !== 6) {
        return "rgba(29, 63, 169, 0.18)";
      }

      const r = parseInt(normalized.substring(0, 2), 16);
      const g = parseInt(normalized.substring(2, 4), 16);
      const b = parseInt(normalized.substring(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
#astra-widget-bubble {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 999999;
  width: 64px;
  height: 64px;
  border: 0;
  border-radius: 50%;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
  color: #fff;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

#astra-widget-bubble:hover {
  transform: scale(1.06);
  box-shadow: 0 20px 46px rgba(0, 0, 0, 0.28);
}

.astra-bubble-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 22px;
  font-weight: 700;
}

.astra-bubble-svg svg {
  width: 30px;
  height: 30px;
  fill: currentColor;
}

#astra-chat-window {
  position: fixed;
  right: 24px;
  bottom: 100px;
  z-index: 999998;
  display: flex;
  flex-direction: column;
  width: 380px;
  max-width: calc(100vw - 24px);
  height: 600px;
  max-height: calc(100vh - 136px);
  overflow: hidden;
  border: 1px solid;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
  backdrop-filter: blur(14px);
  transform: translateY(12px) scale(0.96);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease, transform 0.22s ease;
}

#astra-chat-window.open {
  transform: translateY(0) scale(1);
  opacity: 1;
  pointer-events: auto;
}

.astra-chat-header {
  padding: 18px 20px;
  color: #fff;
}

.astra-chat-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  text-transform: none;
}

.astra-chat-header small {
  opacity: 0.88;
  text-transform: none;
}

.astra-messages {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  overflow-y: auto;
  background:
    radial-gradient(circle at top right, rgba(29, 63, 169, 0.08), transparent 30%),
    linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
}

.astra-message {
  max-width: 85%;
  padding: 12px 14px;
  border-radius: 18px;
  text-transform: none;
  white-space: pre-wrap;
  word-break: break-word;
}

.astra-message ::v-deep strong {
  font-weight: 700 !important;
}

.astra-message.ai {
  align-self: flex-start;
  color: #1f2937;
  background: #fff;
  border-bottom-left-radius: 6px;
}

.astra-message.user {
  align-self: flex-end;
  color: #fff;
  background: #1d3fa9;
  border-bottom-right-radius: 6px;
}

.astra-input-area {
  display: flex;
  gap: 10px;
  padding: 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  background: #fff;
}

.astra-input-area input {
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #d0d8e6;
  border-radius: 14px;
  outline: none;
  text-transform: none;
}

.astra-input-area input:focus {
  border-color: #1d3fa9;
}

.astra-send-btn {
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 14px;
  color: #fff;
  font-size: 22px;
  line-height: 1;
}

.astra-send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 767px) {
  #astra-widget-bubble {
    right: 16px;
    bottom: 16px;
  }

  #astra-chat-window {
    right: 12px;
    left: 12px;
    bottom: 90px;
    width: auto;
    max-width: none;
    height: 70vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AstraChatbot.vue